import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import { Download, Calendar, Filter, TrendingUp } from 'lucide-react';

const Reports = () => {
  const [salesData, setSalesData] = useState({
    total_sales: 0,
    total_invoices: 0,
    total_estimates: 0,
    monthly_breakdown: [],
    top_products: []
  });
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    salesperson_id: ''
  });
  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    if (!dataFetched) {
      setDataFetched(true);
      fetchSalespersons();
      
      // Set default date range (last 30 days)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      setFilters({
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        salesperson_id: ''
      });
    }
  }, [dataFetched]);

  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      fetchSalesReport();
    }
  }, [filters]);

  const fetchSalespersons = async (retryCount = 0) => {
    try {
      const response = await axios.get('/sales/salespersons');
      setSalespersons(response.data);
    } catch (error) {
      console.error('Failed to load salespersons:', error);
      
      // Handle 429 with retry logic
      if (error.response?.status === 429 && retryCount < 1) {
        setTimeout(() => {
          fetchSalespersons(retryCount + 1);
        }, 1000);
        return;
      }
      
      // Use fallback sample data
      const sampleSalespersons = [
        { id: 1, name: 'Sales Person 1' },
        { id: 2, name: 'Sales Person 2' }
      ];
      setSalespersons(sampleSalespersons);
      
      if (error.response?.status === 429) {
        toast('Server is busy, using sample data', { icon: '⚠️' });
      } else {
        toast('Using sample data - API connection failed', { icon: '⚠️' });
      }
    }
  };

  const fetchSalesReport = async (retryCount = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.salesperson_id) params.append('salesperson_id', filters.salesperson_id);

      const response = await axios.get(`/admin/reports/sales?${params}`);
      setSalesData(response.data);
    } catch (error) {
      console.error('Failed to load sales report:', error);
      
      // Handle 429 with retry logic
      if (error.response?.status === 429 && retryCount < 1) {
        setTimeout(() => {
          fetchSalesReport(retryCount + 1);
        }, 1000);
        return;
      }
      
      // Use fallback sample data
      const sampleSalesData = {
        total_sales: 125000,
        total_invoices: 8,
        total_estimates: 15,
        monthly_breakdown: [
          { month: 'Jan', sales: 15000, invoices: 2, estimates: 4 },
          { month: 'Feb', sales: 22000, invoices: 3, estimates: 5 },
          { month: 'Mar', sales: 18000, invoices: 2, estimates: 3 },
          { month: 'Apr', sales: 25000, invoices: 1, estimates: 3 }
        ],
        top_products: [
          { name: 'Product 1', sales: 45000 },
          { name: 'Product 2', sales: 35000 }
        ]
      };
      setSalesData(sampleSalesData);
      
      if (error.response?.status === 429) {
        toast('Server is busy, using sample data', { icon: '⚠️' });
      } else {
        toast('Using sample data - API connection failed', { icon: '⚠️' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateTotals = () => {
    if (!salesData || typeof salesData !== 'object') {
      return { totalRevenue: 0, totalSales: 0, avgSaleValue: 0 };
    }

    const totalRevenue = salesData.total_sales || 0;
    const totalSales = salesData.total_invoices || 0;
    const avgSaleValue = totalSales > 0 ? totalRevenue / totalSales : 0;

    return { totalRevenue, totalSales, avgSaleValue };
  };

  const exportToCSV = () => {
    if (!salesData || !salesData.monthly_breakdown || salesData.monthly_breakdown.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Month', 'Sales', 'Invoices', 'Estimates'];
    const csvContent = [
      headers.join(','),
      ...salesData.monthly_breakdown.map(item => [
        item.month,
        item.sales.toFixed(1),
        item.invoices,
        item.estimates
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_report_${filters.start_date}_to_${filters.end_date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const { totalRevenue, totalSales, avgSaleValue } = calculateTotals();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Sales Reports</h1>
            <p className="text-gray-600">Analyze your sales performance</p>
          </div>
          <button
            onClick={exportToCSV}
            className="btn-primary flex items-center"
            disabled={!salesData.monthly_breakdown || salesData.monthly_breakdown.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex items-center mb-4">
          <Filter className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-slate-900">Filters</h3>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Salesperson
            </label>
            <select
              value={filters.salesperson_id}
              onChange={(e) => handleFilterChange('salesperson_id', e.target.value)}
              className="input-field"
            >
              <option value="">All Salespersons</option>
              {salespersons.map(sp => (
                <option key={sp.id} value={sp.id}>{sp.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                start_date: '',
                end_date: '',
                salesperson_id: ''
              })}
              className="btn-secondary"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-semibold text-slate-900">₹{totalRevenue.toFixed(1)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-[#0A5F38]" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sales</p>
              <p className="text-2xl font-semibold text-slate-900">{totalSales}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Sale Value</p>
              <p className="text-2xl font-semibold text-slate-900">₹{avgSaleValue.toFixed(1)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="card">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-slate-900">Sales Details</h3>
          {loading && <div className="text-sm text-gray-500">Loading...</div>}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {salesData.monthly_breakdown && salesData.monthly_breakdown.map((item, index) => (
                <tr key={index} className="hover:bg-white">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.month} 2025
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    #{(index + 1).toString().padStart(3, '0')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    Monthly Summary
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {item.invoices} invoices, {item.estimates} estimates
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-semibold">
                    ₹{item.sales.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {(!salesData.monthly_breakdown || salesData.monthly_breakdown.length === 0) && !loading && (
            <div className="text-center py-8 text-gray-500">
              No sales data found for the selected criteria.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;


